import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useCan } from "@/lib/perms";
import { EmptyState, Loader } from "@/components/EmptyState";
import { Input } from "@/components/ui/input";
import { Boxes, Plus, Minus, AlertTriangle, RefreshCw } from "lucide-react";

const LOW_STOCK = 5;

export default function Inventory() {
  const { business } = useAuth();
  const canAdjust = useCan()("inventory.edit");
  const navigate = useNavigate();
  const isVendor = business?.business_type === "vendor";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/inventory");
      setItems(r.data.inventory);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <Loader />;
  const filtered = items.filter((it) => it.product_name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h1 className="font-heading text-2xl font-extrabold text-stone-900 sm:text-3xl">Inventory</h1>
        <p className="mt-1 text-stone-500">Stock builds up automatically from delivered orders.{canAdjust ? " Use the − button to record daily usage." : ""}</p>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={Boxes} title="No stock yet" description="When you confirm a delivery (or report a missed delivery), the received quantities are added here automatically." testid="inventory-empty" />
      ) : (
        <>
          <Input data-testid="inventory-search" placeholder="Search items…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm bg-white" />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((it) => <InvCard key={it.id} it={it} onChanged={load} />)}
          </div>
        </>
      )}
    </div>
  );
}

function InvCard({ it, onChanged }) {
  const navigate = useNavigate();
  const can = useCan();
  const canEdit = can("inventory.edit");
  const [step, setStep] = useState("1");
  const [thr, setThr] = useState(String(it.threshold ?? LOW_STOCK));
  const [busy, setBusy] = useState(false);
  const low = (it.quantity || 0) <= (it.threshold ?? LOW_STOCK);

  const adjust = async (sign) => {
    const amt = parseFloat(step);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/${it.id}/adjust`, { delta: sign * amt });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };

  const saveThr = async () => {
    const v = parseFloat(thr);
    if (isNaN(v) || v < 0) { toast.error("Enter a valid threshold"); return; }
    try { await api.post(`/inventory/${it.id}/threshold`, { threshold: v }); toast.success("Alert level updated"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const reorder = () => {
    navigate("/app/orders", { state: { reorder: {
      partner_business_id: it.last_vendor_id,
      items: [{ product_name: it.product_name, unit: it.unit, quantity: it.suggested_reorder > 0 ? it.suggested_reorder : "" }],
      notes: `Reorder of ${it.product_name}${low ? " (stock low)" : ""}`,
    } } });
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-5 shadow-sm" data-testid={`inventory-card-${it.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-heading font-semibold text-stone-900">{it.product_name}</p>
          <p className="text-xs text-stone-500">Unit: {it.unit}</p>
        </div>
        {low && <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-600" data-testid={`inventory-low-${it.id}`}><AlertTriangle className="h-3 w-3" /> Low</span>}
      </div>

      <div className="mt-3">
        <p className="text-xs uppercase tracking-wider text-stone-400">Available</p>
        <p className={`font-heading text-3xl font-extrabold ${low ? "text-rose-600" : "text-[#0D5C3A]"}`} data-testid={`inventory-qty-${it.id}`}>{Number(it.quantity || 0).toLocaleString("en-IN")}</p>
      </div>

      <div className="mt-2 flex gap-3 text-xs text-stone-500">
        <span>Received: <b className="text-stone-700">{Number(it.total_received || 0).toLocaleString("en-IN")}</b></span>
        <span>Used: <b className="text-stone-700">{Number(it.total_used || 0).toLocaleString("en-IN")}</b></span>
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-stone-500">
        <span className="whitespace-nowrap">Alert at ≤</span>
        <Input type="number" min="0" step="0.01" value={thr} onChange={(e) => setThr(e.target.value)} className="h-8 w-16 text-center" disabled={!canEdit} data-testid={`inventory-threshold-${it.id}`} />
        <span>{it.unit}</span>
        {canEdit && <button onClick={saveThr} className="ml-auto rounded-lg border border-[#E5E0D8] px-2 py-1 font-medium text-[#0D5C3A] hover:bg-[#F0ECE1]" data-testid={`inventory-threshold-save-${it.id}`}>Save</button>}
      </div>
      {it.suggested_reorder > 0 && (
        <p className="mt-2 text-xs text-stone-500" data-testid={`inventory-suggested-${it.id}`}>Suggested reorder: <b className="text-stone-700">{Number(it.suggested_reorder).toLocaleString("en-IN")} {it.unit}</b> <span className="text-stone-400">(based on recent usage)</span></p>
      )}

      {canEdit && <div className="mt-4 flex items-center gap-2">
        <button disabled={busy} onClick={() => adjust(-1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E0D8] text-rose-600 hover:bg-rose-50 disabled:opacity-50" data-testid={`inventory-minus-${it.id}`} title="Record usage"><Minus className="h-4 w-4" /></button>
        <Input type="number" min="0" step="0.01" value={step} onChange={(e) => setStep(e.target.value)} className="h-9 w-20 text-center" data-testid={`inventory-step-${it.id}`} />
        <button disabled={busy} onClick={() => adjust(1)} className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E5E0D8] text-[#0D5C3A] hover:bg-[#F0ECE1] disabled:opacity-50" data-testid={`inventory-plus-${it.id}`} title="Add stock"><Plus className="h-4 w-4" /></button>
      </div>}

      {it.last_vendor_id && can("procurement.create") && (
        <button onClick={reorder} data-testid={`inventory-reorder-${it.id}`}
          className={`mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${low ? "bg-[#0D5C3A] text-white hover:bg-[#084229]" : "border border-[#E5E0D8] text-[#0D5C3A] hover:bg-[#F0ECE1]"}`}>
          <RefreshCw className="h-4 w-4" /> Reorder from {it.last_vendor_name}
        </button>
      )}
    </div>
  );
}
